import React from "react";
import round from "lodash/round";
import srmColorLookup from "../../../constants/srmColorLookup.json";
import i18n from "@breward/i18n";

interface ColorPaletteInputProps {
  name?: string;
  min?: number;
  max?: number;
  optionsCount?: number;
  unit?: "srm" | "ebc";
  mode?: "single" | "range";
  value?: number | number[];
  onChange?: (val: number | number[]) => void;
  readOnly?: boolean;
  withLabels?: boolean;
}

export const srmToEbc = (srm: number) => Math.round(srm * 1.97);
export const ebcToSrm = (ebc: number) => round(ebc / 1.97, 1);

export const ColorPaletteInput = ({
  name,
  min = 2,
  max = 40,
  unit = "ebc",
  value,
  onChange,
  readOnly = false,
}: ColorPaletteInputProps) => {
  // Convert min/max range to EBC if unit === "ebc" and bounds were given in SRM (0-40)
  const minVal = unit === "ebc" && min <= 40 ? srmToEbc(min) : min;
  const maxVal = unit === "ebc" && max <= 40 ? srmToEbc(max) : max;

  const valNum = typeof value === "number" ? value : Number(value) || minVal;

  // Convert current value back to SRM for color lookup in srmColorLookup.json
  const srmVal = unit === "ebc" ? ebcToSrm(valNum) : valNum;
  const srmKey = Math.min(40, Math.max(0, round(srmVal, 1))).toFixed(1);
  const rgbString =
    (srmColorLookup as Record<string, string>)[srmKey] ||
    (srmColorLookup as Record<string, string>)[Math.round(srmVal).toString()] ||
    "243,249,147";

  const activeColor = `rgb(${rgbString})`;

  const gradientStops = [
    "rgb(248,247,83)", // 2 SRM (~4 EBC)
    "rgb(224,208,27)", // 5 SRM (~10 EBC)
    "rgb(191,129,58)", // 10 SRM (~20 EBC)
    "rgb(152,83,54)", // 16 SRM (~31 EBC)
    "rgb(93,52,26)", // 20 SRM (~39 EBC)
    "rgb(38,23,22)", // 25 SRM (~49 EBC)
    "rgb(16,11,10)", // 30 SRM (~59 EBC)
    "rgb(3,4,3)", // 40 SRM (~79 EBC)
  ];
  const ebcGradient = `linear-gradient(to right, ${gradientStops.join(",")})`;

  return (
    <div className="font-sans rounded-xl border border-primary-100 bg-white p-4 shadow-sm dark:border-content-700 dark:bg-content-800">
      <div className="flex items-center gap-4">
        <div
          className="h-12 w-12 shrink-0 rounded-xl border border-primary-200 shadow-inner transition-all dark:border-content-600"
          style={{ backgroundColor: activeColor }}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center justify-between text-xs font-semibold text-content-700 dark:text-content-300">
            <span>{i18n.t("global:shared.beerColor", "Barwa piwa")}</span>
            <span className="font-mono text-base font-bold text-primary-800 dark:text-primary-200">
              {valNum}{" "}
              <span className="text-xs font-normal text-content-400">
                {unit.toUpperCase()}
              </span>
            </span>
          </div>
          <input
            type="range"
            disabled={readOnly}
            min={minVal}
            max={maxVal}
            value={valNum}
            onChange={(e) => onChange && onChange(Number(e.target.value))}
            style={{ background: ebcGradient }}
            className="h-3.5 w-full cursor-pointer appearance-none rounded-lg accent-primary-600 focus:outline-none disabled:opacity-50"
          />
        </div>
      </div>
    </div>
  );
};


